import { cn } from "@/lib/cn";
import { contrast, ratingFor } from "@/api/contrast";
import { PALETTES } from "@/api/palettes";
import type { Palette } from "@/api/types";
import { AlertIcon, CheckIcon } from "./icons";

type Props = {
  value: string;
  onChange: (hex: string) => void;
  onPalette?: (p: Palette) => void;
};

/**
 * Hex colour picker with a curated palette strip and a live WCAG
 * contrast badge against white. The badge is the soft guardrail —
 * lets users pick whatever they want, but flags poor accent choices.
 *
 * Deliberately compact: small swatch + hex on one row, contrast badge
 * inline, palettes as a tight 4-up swatch grid. Keeps the accent step
 * from dominating the controls column even when expanded.
 */
export function ColourPicker({ value, onChange, onPalette }: Props) {
  const ratio = contrast(value || "#000000", "#FFFFFF");
  const rating = ratingFor(ratio);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-label="Pick colour"
          className="h-8 w-9 cursor-pointer rounded border border-[var(--border-2)] bg-white p-0.5"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={7}
          aria-label="Hex code"
          className="w-24 rounded border border-[var(--border-2)] bg-white px-2 py-1.5 font-mono text-xs uppercase outline-none focus-visible:border-fg-1 focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]"
        />
        <span
          className={cn(
            "inline-flex flex-1 items-center justify-center gap-1 rounded-full px-2 py-1 text-2xs font-semibold",
            rating.lvl === "ok" && "bg-[#E3F2EA] text-[color:var(--ok)]",
            rating.lvl === "warn" && "bg-[#FBEFD8] text-[#6B4200]",
            rating.lvl === "bad" && "bg-[#FAE3E1] text-[color:var(--danger)]",
          )}
          title={`Contrast on white: ${rating.help} · ${rating.label}`}
        >
          {rating.lvl === "ok" ? (
            <CheckIcon width={10} height={10} strokeWidth={2.5} />
          ) : (
            <AlertIcon width={10} height={10} />
          )}
          {rating.help} · {rating.label}
        </span>
      </div>

      <div className="grid grid-cols-4 gap-1.5">
        {PALETTES.map((p) => {
          const sel = value === p.colors[0];
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => (onPalette ? onPalette(p) : onChange(p.colors[0]))}
              title={p.name}
              aria-label={p.name}
              className={cn(
                "rounded border bg-white p-1 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]",
                sel
                  ? "border-[var(--studio)] ring-1 ring-[var(--studio)]"
                  : "border-[var(--border-1)] hover:border-[var(--studio)]",
              )}
            >
              <div className="flex h-5 gap-0.5">
                {p.colors.map((c, i) => (
                  <span
                    key={i}
                    className="flex-1 rounded-sm"
                    style={{ background: c }}
                    aria-hidden
                  />
                ))}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
